import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../lib/api';
import { TaskHolderName } from '../components/TaskHolder';
import { enumLabel, useI18n } from '../lib/i18n';
import { P, useAuth } from '../lib/auth';
import { formatDuration, formatMoney, formatNumber, formatPercent, isoDate } from '../lib/format';
import { isOpenUrgent } from '../lib/task-order';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
} from '../components/ui';

/** Sentinel's categorical series — the `chart` scale from the design tokens. */
const SERIES = ['#C7102E', '#22D3EE', '#F9C751', '#7C5CFF', '#17B980', '#FF7A45', '#76777A', '#EE5468'];

export function ReportsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [tab, setTab] = useState('operations');
  const firstOfMonth = `${new Date().toISOString().slice(0, 7)}-01`;
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(isoDate());

  return (
    <>
      <PageHeader
        title={t('reports.title')}
        subtitle={can(P.USERS_VIEW_ALL) ? undefined : t('reach.figuresNote')}
        action={
          <div className="flex items-center gap-2">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="!w-auto" />
            <span className="text-muted">—</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="!w-auto" />
          </div>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'operations', label: t('reports.operations') },
          { id: 'workforce', label: t('reports.workforce') },
          // The finance reports — finance, revenue, receivables and the client
          // statement — moved to sys with the money they describe.
        ]}
      />

      <div className="mt-5">
        {tab === 'operations' && <OperationsReport from={from} to={to} />}
        {tab === 'workforce' && <WorkforceReport from={from} to={to} />}
      </div>
    </>
  );
}

function OperationsReport({ from, to }: { from: string; to: string }) {
  const { t, locale, localized } = useI18n();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', 'operations', from, to],
    queryFn: () => api.get<any>('/reports/operations', { from, to }),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const sla = data.slaCompliance;

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric label={t('nav.tickets')} value={formatNumber(sla.totalTickets, locale)} />
        <Metric
          label={t('dash.slaCompliance')}
          value={formatPercent(sla.assignment.compliancePercent, locale)}
          hint={`${sla.assignment.onTime} ${t('reports.onTime').toLowerCase()}`}
          tone={sla.assignment.compliancePercent >= 90 ? 'success' : sla.assignment.compliancePercent >= 70 ? 'warning' : 'error'}
        />
        <Metric
          label={t('reports.avgViewDelay')}
          value={formatDuration(sla.view.averageDelaySeconds)}
          hint={`${sla.view.onTimeViews} ${t('reports.onTime').toLowerCase()} / ${sla.view.lateViews} ${t('reports.late').toLowerCase()}`}
        />
        <Metric
          label={t('reports.qThroughput')}
          value={formatNumber(data.qTasks.completedTotal, locale)}
          hint={enumLabel('DONE', locale)}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('dash.ticketsByStatus')} />
          <ChartBars
            rows={data.ticketsByStatus.map((r: any) => ({ label: enumLabel(r.status, locale), value: r.count }))}
          />
        </Card>
        <Card>
          <CardHeader title={t('dash.tasksByStatus')} />
          <ChartBars
            rows={data.tasksByStatus.map((r: any) => ({ label: enumLabel(r.status, locale), value: r.count }))}
          />
        </Card>
        <Card>
          <CardHeader title="Tickets by department" />
          <ChartPie
            rows={data.ticketsByDepartment.map((r: any) => ({ label: localized(r.department), value: r.count }))}
          />
        </Card>
        <Card padded={false}>
          <CardHeader title={t('dash.viewBreaches')} className="p-5 pb-0" />
          {data.viewViolations.length ? (
            <Table>
              <thead>
                <tr>
                  <Th>{t('nav.employees')}</Th>
                  <Th align="end">{t('violations.violations')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.viewViolations.map((row: any, index: number) => (
                  <Tr key={row.user?.id ?? index}>
                    <Td>{localized(row.user)}</Td>
                    <Td align="end" className="tabular text-error-fg dark:text-error-dark">{row.count}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <EmptyState title={t('dash.nothingPending')} hint="" />
          )}
        </Card>
      </div>

      {data.overdueTasks.length > 0 && (
        <Card padded={false}>
          <CardHeader title={t('reports.overdueTasks')} className="p-5 pb-0" />
          <Table>
            <thead>
              <tr>
                <Th>{t('common.name')}</Th>
                <Th>{t('common.project')}</Th>
                <Th align="end">{t('common.assignee')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.overdueTasks.slice(0, 20).map((task: any) => (
                <Tr key={task.id}>
                  <Td>
                    {/* The urgent ones come first (2026-10-11); this says why. */}
                    <span className="flex items-center gap-2">
                      <span className="min-w-0">{task.title}</span>
                      {isOpenUrgent(task) && <Badge tone="error">{enumLabel('URGENT', locale)}</Badge>}
                    </span>
                  </Td>
                  <Td className="text-muted">{task.project ? localized(task.project) : t('common.general')}</Td>
                  <Td align="end" className="text-muted"><TaskHolderName task={task} empty="—" /></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function WorkforceReport({ from, to }: { from: string; to: string }) {
  const { t, locale, localized } = useI18n();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reports', 'workforce', from, to],
    queryFn: () => api.get<any>('/reports/workforce', { from, to }),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card padded={false}>
        <CardHeader title={t('reports.overtimeByEmployee')} className="p-5 pb-0" />
        {data.overtime.length ? (
          <Table>
            <thead>
              <tr>
                <Th>{t('nav.employees')}</Th>
                <Th align="end">Regular</Th>
                <Th align="end">{t('payroll.overtime')}</Th>
                <Th align="end">{t('payroll.holidayWork')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.overtime.map((row: any, index: number) => (
                <Tr key={row.user?.id ?? index}>
                  <Td>{localized(row.user)}</Td>
                  <Td align="end" className="tabular text-muted">{row.regularHours}h</Td>
                  <Td align="end" className="tabular">{row.overtimeHours}h</Td>
                  <Td align="end" className="tabular text-muted">{row.holidayHours}h</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState />
        )}
      </Card>

      <Card padded={false}>
        <CardHeader title={t('reports.penaltiesByEmployee')} className="p-5 pb-0" />
        {data.penalties.length ? (
          <Table>
            <thead>
              <tr>
                <Th>{t('nav.employees')}</Th>
                <Th align="end">{t('violations.penalties')}</Th>
                <Th align="end">{t('common.total')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.penalties.map((row: any, index: number) => (
                <Tr key={row.user?.id ?? index}>
                  <Td>{localized(row.user)}</Td>
                  <Td align="end" className="tabular">{row.count}</Td>
                  <Td align="end" className="tabular text-error-fg dark:text-error-dark">{formatMoney(row.total, 'EGP', locale)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState title={t('dash.nothingPending')} hint="" />
        )}
      </Card>
    </div>
  );
}

function ChartBars({ rows }: { rows: Array<{ label: string; value: number }> }) {
  const { t } = useI18n();
  if (!rows.length) return <EmptyState title={t('common.noResults')} hint="" />;

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
            axisLine={{ stroke: 'var(--line)' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: 'var(--text-tertiary)', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--line)',
              borderRadius: 10,
              color: 'var(--text-primary)',
              fontSize: 13,
            }}
            cursor={{ fill: 'rgba(199,16,46,0.08)' }}
          />
          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
            {rows.map((_, index) => (
              <Cell key={index} fill={SERIES[index % SERIES.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChartPie({ rows, money = false }: { rows: Array<{ label: string; value: number }>; money?: boolean }) {
  const { t, locale } = useI18n();
  if (!rows.length) return <EmptyState title={t('common.noResults')} hint="" />;

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="label" innerRadius={50} outerRadius={85} paddingAngle={2}>
            {rows.map((_, index) => (
              <Cell key={index} fill={SERIES[index % SERIES.length]} stroke="var(--surface-card)" strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: any) => (money ? formatMoney(value, 'EGP', locale) : formatNumber(value, locale))}
            contentStyle={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--line)',
              borderRadius: 10,
              color: 'var(--text-primary)',
              fontSize: 13,
            }}
          />
          <Legend
            verticalAlign="bottom"
            iconType="circle"
            wrapperStyle={{ fontSize: 12, color: 'var(--text-secondary)' }}
            // Recharts paints each label in its series' colour, which a dark card or a
            // pale series cannot carry: the dot keeps the colour, the words stay readable.
            formatter={(value: any) => <span className="text-[var(--text-secondary)]">{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
